import React, {
  PropsWithChildren,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from "react";
import * as styles from "./PackageCard.module.scss";
import {
  BlockContentValue,
  BackgroundColor
} from "@silversea-ssc/ui-components-react";
import TextBox from "components/TextBox/TextBox";
import useIsMounted from "src/useIsMounted";
import { useDebouncedCallback } from "use-debounce";

type Props = {
  backgroundColor?: BackgroundColor;
  title?: string;
  subtitle?: BlockContentValue;
  footer?: ReactElement;
  ctaButton?: ReactElement;
};

const PackageCard: React.FC<PropsWithChildren<Props>> = ({
  title,
  subtitle,
  footer,
  children,
  ctaButton
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEnsureSameCardHeadersHeight(ref);

  return (
    <article className={styles.card} ref={ref}>
      <header className={styles.header}>
        <div className={styles.title}>{title}</div>
        <div className={styles.subtitle}>
          <TextBox fontSize="inherit" content={subtitle} />
        </div>
      </header>
      <div className={styles.content}>{children}</div>
      <footer className={styles.footer}>
        {footer}
        <div className={styles.cta}>{ctaButton}</div>
      </footer>
    </article>
  );
};

export default React.memo(PackageCard);

const useEnsureSameCardHeadersHeight = (ref: React.RefObject<HTMLElement>) => {
  const isMounted = useIsMounted();

  const isFirstCard = useMemo(() => checkIsFirstCard(ref.current), [ref]);

  const doResize = useCallback(() => {
    if (!isMounted || !isFirstCard) return;

    const card = ref.current;
    const swiperWrapper = card?.parentElement;
    const cardsContainer = swiperWrapper?.parentElement;

    forceSameHeadersHeight(cardsContainer);
  }, [isMounted, isFirstCard, ref]);

  const handleResize = useDebouncedCallback(doResize, 200);

  useEffect(() => {
    if (!isMounted || !isFirstCard) return;

    window.addEventListener("scroll", handleResize);
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("scroll", handleResize);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [handleResize, isFirstCard, isMounted]);
};

const checkIsFirstCard = (card: HTMLElement | null | undefined) => {
  const swiperWrapper = card?.parentElement;
  const cardsContainer = swiperWrapper?.parentElement;

  return cardsContainer?.firstElementChild === swiperWrapper;
};

const forceSameHeadersHeight = (
  cardsContainer: HTMLElement | null | undefined
) => {
  const allHeaders = cardsContainer?.querySelectorAll("header") || [];
  Array.from(allHeaders).forEach((header) => {
    header.style.height = "auto";
  });
  const maxHeight = Math.max(
    ...Array.from(allHeaders).map((header) => header.clientHeight)
  );
  Array.from(allHeaders).forEach((header) => {
    header.style.height = `${maxHeight}px`;
  });
};
